import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, MapPin, Car, Plus, Trash2, Lock, Unlock, ChevronLeft, ChevronRight, Music, MapPinCheck, CheckCircle2, Loader2, AlertTriangle, AlertCircle, Users, RefreshCw, Sparkles, ArrowRight, CheckCheck, ExternalLink } from "lucide-react";
import { api, apiError, formatDate, getGeo } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/components/PlanGate";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// Prioridade de "cor do dia" na tira: confirmado > pendente > indisponível > flexível > livre
const dayDot = (items) => {
  if (!items?.length) return null;
  if (items.some((i) => i.my_status === "confirmado")) return "bg-emerald-500";
  if (items.some((i) => ["aprovado", "suplente", "convidado", "aguardando aprovação"].includes(i.my_status))) return "bg-amber-500";
  if (items.some((i) => i.type === "personal" && i.kind !== "flexivel")) return "bg-rose-500";
  if (items.some((i) => i.type === "personal")) return "bg-accent";
  if (items.some((i) => i.type === "created_event")) return items.some((i) => i.type === "created_event" && (i.team?.open || 0) > 0) ? "bg-amber-500" : "bg-[#FF4D00]";
  return "bg-zinc-500";
};

// my_status (aplicação) -> props do StatusBadge acessível
const statusBadge = (s) => {
  switch (s) {
    case "confirmado": return { status: "confirmado" };
    case "aprovado": return { status: "aguardando", label: "Aprovada — confirmar" };
    case "suplente": return { status: "aguardando", label: "Suplente" };
    case "convidado": return { status: "aguardando", label: "Convite recebido" };
    case "concluido": return { status: "concluido" };
    default: return null;
  }
};

export default function Agenda() {
  const { hasFeature, user } = useAuth();
  const [items, setItems] = useState([]);
  const [insights, setInsights] = useState(null);
  const [profileFilter, setProfileFilter] = useState("all");
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(iso(new Date()));
  const [form, setForm] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [checkingIn, setCheckingIn] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const navigate = useNavigate();
  const utype = user?.user_type;

  const load = () => api.get("/agenda").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { if (hasFeature("minha_agenda")) load(); }, [hasFeature]);
  useEffect(() => {
    if (!hasFeature("minha_agenda")) return;
    api.get("/agenda/insights", { params: { date: selectedDay } }).then((r) => setInsights(r.data)).catch(() => {});
  }, [selectedDay, items, hasFeature]);

  const filtered = useMemo(() => (profileFilter === "all" ? items : items.filter((i) => i.role === profileFilter)), [items, profileFilter]);
  const byDate = useMemo(() => filtered.reduce((a, e) => { (a[e.date] = a[e.date] || []).push(e); return a; }, {}), [filtered]);

  if (!hasFeature("minha_agenda")) return <PlanGate feature="minha_agenda" />;

  const saveForm = async () => {
    if (!form.title?.trim()) return toast.error("Informe o título.");
    try {
      if (form.id) await api.put(`/commitments/${form.id}`, form);
      else await api.post("/commitments", form);
      toast.success("Compromisso salvo.");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delCommitment = async (id) => {
    try { await api.delete(`/commitments/${id}`); toast.success("Compromisso removido."); load(); setDayDetail(null); setForm(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openNew = (date) => setForm({ title: "", date: date || selectedDay, end_date: date || selectedDay, start_time: "20:00", end_time: "23:00", kind: "indisponivel", note: "" });
  const openEdit = (it) => setForm({ id: it.commitment_id || it.id, title: it.name, date: it.date, end_date: it.end_date || it.date, start_time: it.start_time, end_time: it.end_time, kind: it.kind, note: it.note || "" });
  const markAvailable = () => setForm({ title: "Disponível para shows", date: selectedDay, end_date: selectedDay, start_time: "08:00", end_time: "23:00", kind: "flexivel", note: "" });
  const blockTime = () => openNew(selectedDay);

  const syncCalendar = () => {
    const evs = filtered.filter((i) => i.start_time && i.date);
    if (!evs.length) return toast.info("Nada para sincronizar neste filtro.");
    const dt = (d, t) => d.replaceAll("-", "") + "T" + (t || "00:00").replace(":", "") + "00";
    const esc = (s) => String(s || "").replace(/[,;\\]/g, " ").replace(/\n/g, " ");
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Mi Chamou//Agenda//PT", "CALSCALE:GREGORIAN"];
    evs.forEach((i, idx) => lines.push("BEGIN:VEVENT", `UID:michamou-${i.id}-${idx}@michamou`,
      `DTSTART:${dt(i.date, i.start_time)}`, `DTEND:${dt(i.end_date || i.date, i.end_time || i.start_time)}`,
      `SUMMARY:${esc(i.name || "Compromisso")}`, `LOCATION:${esc(i.address)}`, "END:VEVENT"));
    lines.push("END:VCALENDAR");
    const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar" }));
    const a = document.createElement("a"); a.href = url; a.download = "michamou-agenda.ics"; a.click(); URL.revokeObjectURL(url);
    toast.success("Calendário exportado (.ics). Importe no Google/Apple Agenda.");
  };

  const openSync = async () => {
    setSyncOpen(true);
    if (feedUrl) return;
    try {
      const r = await api.post("/agenda/feed-url");
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setFeedUrl(base.replace(/^https?:/, "webcal:") + r.data.path);
    } catch { /* usuário ainda pode baixar o .ics */ }
  };
  const copyFeed = () => { navigator.clipboard?.writeText(feedUrl.replace(/^webcal:/, "https:")); toast.success("Link copiado!"); };

  const doCheckin = async (it) => {
    setCheckingIn(it.app_id);
    try {
      const geo = await getGeo();
      await api.post(`/applications/${it.app_id}/checkin`, geo);
      toast.success("Check-in realizado!");
      load();
    } catch (e) {
      toast.error(e?.response ? apiError(e.response?.data?.detail) : "Não foi possível obter sua localização. Ative o GPS e tente novamente.");
    } finally { setCheckingIn(null); }
  };

  // Semana com início na segunda-feira
  const weekDays = () => {
    const offset = (cursor.getDay() + 6) % 7;
    const start = addDays(cursor, -offset);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };
  const shiftWeek = (n) => { const c = addDays(cursor, 7 * n); setCursor(c); setSelectedDay(iso(addDays(c, -((c.getDay() + 6) % 7)))); };

  // Grade de mês
  const monthGrid = () => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const startOffset = new Date(y, m, 1).getDay();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    const days = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
    return cells;
  };
  const shiftMonth = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));

  const dayItems = (byDate[selectedDay] || []);
  const checkinNow = dayItems.filter((it) => it.type !== "personal" && it.my_status === "confirmado" && it.checkin_available && !it.checkin);
  const selectedLabel = cap(new Date(selectedDay + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }));

  const inp = "bg-[#09090B] border-white/10 rounded-xl";

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-6" data-testid="agenda-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><CalendarClock className="h-8 w-8 text-accent" />Minha Agenda</h1>
          <p className="text-muted-foreground mt-1">Seus compromissos, tudo em um só lugar.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="sync-calendar-btn" onClick={openSync} variant="outline" size="sm" aria-label="Sincronizar calendário"
            className="h-9 rounded-xl bg-transparent border-white/10 gap-1.5 text-muted-foreground hover:text-foreground"><RefreshCw className="h-4 w-4" /><span className="hidden sm:inline">Sincronizar</span></Button>
          <Button data-testid="add-commitment-top-btn" onClick={() => openNew(selectedDay)} size="sm" aria-label="Novo compromisso"
            className="h-9 rounded-xl bg-[#FF4D00] hover:bg-[#E64500] text-white gap-1.5"><Plus className="h-4 w-4" />Compromisso</Button>
        </div>
      </div>

      {/* Filtro de perfil (apenas Ambos) */}
      {utype === "both" && (
        <div className="grid grid-cols-4 gap-1 p-1 rounded-2xl bg-card border border-white/10" data-testid="profile-filter">
          {[["all", "Tudo"], ["creator", "Criador"], ["freelancer", "Freelancer"], ["personal", "Pessoal"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setProfileFilter(v)}
              className={`py-2 rounded-xl text-xs font-semibold transition-all ${profileFilter === v ? "bg-[#FF4D00] text-white shadow-[0_0_14px_rgba(255,79,0,0.3)]" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      )}

      {/* Seu dia + Atenção (inteligência acima do calendário) */}
      <SeuDia insights={insights} utype={utype} onAvailable={markAvailable} onBlock={blockTime} navigate={navigate} />
      {insights?.attention?.length > 0 && (
        <div className="space-y-2" data-testid="attention-block">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5 text-accent" />Precisa da sua atenção</div>
          {insights.attention.map((it, i) => <AttentionItem key={i} it={it} idx={i} navigate={navigate} />)}
        </div>
      )}

      {/* Toggle Semana / Mês */}
      <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-card border border-white/10">
        {[["week", "Semana"], ["month", "Mês"]].map(([v, l]) => (
          <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
            className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${view === v ? "bg-[#FF4D00] text-white shadow-[0_0_20px_rgba(255,79,0,0.35)]" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {/* ===== SEMANA ===== */}
      {view === "week" && (
        <>
          {/* Tira de dias */}
          <div className="flex items-center gap-1.5">
            <button data-testid="week-prev" aria-label="Semana anterior" onClick={() => shiftWeek(-1)} className="p-2 rounded-xl border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20 transition-colors shrink-0"><ChevronLeft className="h-4 w-4" /></button>
            <div className="grid grid-cols-7 gap-1.5 flex-1">
              {weekDays().map((d) => {
                const ds = iso(d);
                const dot = dayDot(byDate[ds]);
                const active = ds === selectedDay;
                const isToday = ds === iso(new Date());
                return (
                  <button key={ds} data-testid={`week-day-${ds}`} onClick={() => setSelectedDay(ds)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all ${active ? "border-accent bg-accent/10 shadow-[0_0_15px_rgba(255,170,0,0.25)]" : isToday ? "border-white/20 bg-card" : "border-white/10 bg-card hover:border-white/20"}`}>
                    <span className={`text-[10px] font-semibold uppercase ${active ? "text-accent" : "text-muted-foreground"}`}>{WD[(d.getDay() + 6) % 7]}</span>
                    <span className={`text-lg font-bold leading-none ${active ? "text-accent" : "text-foreground"}`}>{d.getDate()}</span>
                    <span className={`h-1.5 w-1.5 rounded-full ${dot || "bg-transparent"}`} />
                  </button>
                );
              })}
            </div>
            <button data-testid="week-next" aria-label="Próxima semana" onClick={() => shiftWeek(1)} className="p-2 rounded-xl border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20 transition-colors shrink-0"><ChevronRight className="h-4 w-4" /></button>
          </div>

          {/* Cabeçalho do dia */}
          <div className="flex items-end justify-between gap-3">
            <h2 className="font-head text-lg font-bold">{selectedLabel}</h2>
            <span className="text-sm text-muted-foreground shrink-0">{dayItems.length} {dayItems.length === 1 ? "compromisso" : "compromissos"}</span>
          </div>

          {/* Bloco de check-in */}
          {checkinNow.map((it) => (
            <div key={`ci-${it.app_id}`} data-testid={`checkin-block-${it.app_id}`}
              className="rounded-2xl border border-accent/40 bg-accent/[0.07] p-4 shadow-[0_0_20px_rgba(255,170,0,0.15)]">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0"><MapPinCheck className="h-5 w-5 text-accent" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-head font-bold">Check-in disponível</span>
                    <StatusBadge status="checkin_disponivel" pulse />
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 truncate">{it.name} · {it.start_time}</p>
                </div>
              </div>
              <Button data-testid={`checkin-btn-${it.app_id}`} onClick={() => doCheckin(it)} disabled={checkingIn === it.app_id}
                className="w-full mt-3 bg-accent text-zinc-950 font-bold rounded-xl hover:bg-accent/90 gap-2">
                {checkingIn === it.app_id ? <><Loader2 className="h-4 w-4 animate-spin" />Localizando…</> : <><MapPinCheck className="h-4 w-4" />Fazer check-in</>}
              </Button>
            </div>
          ))}

          {/* Lista de eventos do dia */}
          {dayItems.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-card p-8 text-center">
              <CalendarClock className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Dia livre. Nenhum compromisso.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dayItems.map((it) => (
                <AgendaItem key={it.id} it={it} navigate={navigate} onEdit={() => openEdit(it)} onDel={() => delCommitment(it.commitment_id || it.id)} />
              ))}
            </div>
          )}

          {/* Adicionar compromisso */}
          <button data-testid="new-commitment-btn" onClick={() => openNew(selectedDay)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-white/15 text-muted-foreground hover:text-accent hover:border-accent/40 transition-colors text-sm font-medium">
            <Plus className="h-4 w-4" />Adicionar compromisso
          </button>
        </>
      )}

      {/* ===== MÊS ===== */}
      {view === "month" && (
        <>
          <div className="flex items-center justify-center gap-3">
            <button data-testid="cal-prev" aria-label="Mês anterior" onClick={() => shiftMonth(-1)} className="p-2 rounded-xl border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-semibold capitalize min-w-[150px] text-center">{cursor.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</span>
            <button data-testid="cal-next" aria-label="Próximo mês" onClick={() => shiftMonth(1)} className="p-2 rounded-xl border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div data-testid="month-grid" className="grid grid-cols-7 gap-1.5">
            {["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => <div key={i} className="text-center text-[10px] uppercase text-muted-foreground/70 py-1 font-semibold">{d}</div>)}
            {monthGrid().map((d, i) => {
              if (!d) return <div key={i} />;
              const ds = iso(d);
              const dItems = byDate[ds] || [];
              const isToday = ds === iso(new Date());
              return (
                <button key={i} data-testid={`day-${ds}`} onClick={() => { setSelectedDay(ds); setDayDetail(ds); }}
                  className={`min-h-[62px] rounded-xl border p-1.5 text-left transition-colors bg-card hover:border-accent/40 ${isToday ? "border-[#FF4D00]/60 ring-1 ring-[#FF4D00]/30" : "border-white/10"}`}>
                  <div className={`text-xs font-medium ${isToday ? "text-[#FF4D00]" : "text-foreground"}`}>{d.getDate()}</div>
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {dItems.slice(0, 4).map((it) => <span key={it.id} className={`h-1.5 w-1.5 rounded-full ${dayDot([it])}`} />)}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Confirmado</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Pendente</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" />Indisponível</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-accent" />Flexível</span>
          </div>
        </>
      )}

      {/* Detalhe do dia (toque no mês) */}
      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="bg-[#121217] border-white/10 rounded-3xl max-w-lg" data-testid="day-detail">
          <DialogHeader><DialogTitle>{dayDetail && cap(formatDate(dayDetail))}</DialogTitle><DialogDescription>Compromissos e eventos deste dia.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {(byDate[dayDetail] || []).length === 0 ? <p className="text-sm text-muted-foreground">Dia livre.</p> :
              (byDate[dayDetail] || []).map((it) => <AgendaItem key={it.id} it={it} navigate={navigate} onEdit={() => { openEdit(it); setDayDetail(null); }} onDel={() => delCommitment(it.commitment_id || it.id)} />)}
            <Button onClick={() => { openNew(dayDetail); setDayDetail(null); }} variant="outline" className="w-full rounded-xl bg-transparent border-white/10 gap-1 mt-1"><Plus className="h-4 w-4" />Adicionar compromisso pessoal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Formulário de compromisso */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-[#121217] border-white/10 rounded-3xl" data-testid="commitment-form">
          <DialogHeader><DialogTitle>{form?.id ? "Editar compromisso" : "Novo compromisso pessoal"}</DialogTitle><DialogDescription>Bloqueie um horário (Indisponível) ou mantenha-se aberto a oportunidades (Flexível).</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-3">
              <Input data-testid="commitment-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título *" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-muted-foreground">Data início</label><Input data-testid="commitment-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
                <div><label className="text-xs text-muted-foreground">Data fim</label><Input data-testid="commitment-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inp} /></div>
                <div><label className="text-xs text-muted-foreground">Início</label><Input data-testid="commitment-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inp} /></div>
                <div><label className="text-xs text-muted-foreground">Fim</label><Input data-testid="commitment-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inp} /></div>
              </div>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="commitment-kind" className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121217] border-white/10">
                  <SelectItem value="indisponivel">Indisponível (bloqueia candidaturas)</SelectItem>
                  <SelectItem value="flexivel">Flexível (aceito oportunidades)</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="commitment-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Observação (opcional)" className={inp} />
              <div className="flex gap-2">
                <Button data-testid="commitment-save" onClick={saveForm} className="flex-1 bg-[#FF4D00] hover:bg-[#E64500] rounded-xl">Salvar</Button>
                {form.id && <Button data-testid="commitment-delete" onClick={() => delCommitment(form.id)} variant="outline" className="rounded-xl bg-transparent border-rose-500/30 text-rose-400"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sincronização de calendário */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="bg-[#121217] border-white/10 rounded-3xl" data-testid="sync-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-accent" />Sincronizar calendário</DialogTitle><DialogDescription>Mantenha sua agenda do Mí Chamou no Google, Apple ou Outlook.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-accent/25 bg-accent/[0.05] p-4">
              <div className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-accent" />Assinatura automática <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/20 text-accent">recomendado</span></div>
              <p className="text-xs text-muted-foreground mt-1">Atualiza sozinho (a cada ~6h). Cole este link em "Adicionar por URL" no seu app de calendário.</p>
              {feedUrl ? (
                <>
                  <div className="flex gap-2 mt-3">
                    <Input data-testid="sync-feed-url" readOnly value={feedUrl} className="bg-[#09090B] border-white/10 rounded-xl text-xs" onFocus={(e) => e.target.select()} />
                    <Button data-testid="sync-copy-btn" onClick={copyFeed} variant="outline" className="rounded-xl bg-transparent border-white/10 shrink-0">Copiar</Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3" data-testid="sync-deeplinks">
                    {[
                      ["google", "Google", `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl)}`],
                      ["apple", "Apple", feedUrl],
                      ["outlook", "Outlook", `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl.replace(/^webcal:/, "https:"))}&name=${encodeURIComponent("Mí Chamou")}`],
                    ].map(([k, label, href]) => (
                      <a key={k} data-testid={`sync-add-${k}`} href={href} target="_blank" rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border border-white/10 bg-[#09090B] hover:border-accent/40 transition-colors text-xs font-medium text-foreground">
                        <ExternalLink className="h-4 w-4 text-accent" />{label}
                      </a>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">Google abre "Adicionar por URL". Apple abre o Calendário do dispositivo. Outlook abre "Adicionar da web".</p>
                </>
              ) : <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" />Gerando seu link seguro…</p>}
            </div>
            <div className="flex items-center gap-3"><div className="h-px flex-1 bg-white/10" /><span className="text-[11px] text-muted-foreground">ou</span><div className="h-px flex-1 bg-white/10" /></div>
            <Button data-testid="sync-download-btn" onClick={syncCalendar} variant="outline" className="w-full rounded-xl bg-transparent border-white/10 gap-1.5"><CalendarClock className="h-4 w-4" />Baixar .ics (uma vez)</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AgendaItem = ({ it, navigate, onEdit, onDel }) => {
  if (it.type === "personal") {
    const flex = it.kind === "flexivel";
    return (
      <div data-testid={`agenda-personal-${it.id}`}
        className={`flex items-center gap-3 rounded-2xl border p-3.5 ${flex ? "border-accent/25 bg-accent/[0.04]" : "border-rose-500/25 bg-rose-500/[0.04]"}`}>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${flex ? "bg-accent/15" : "bg-rose-500/15"}`}>
          {flex ? <Unlock className="h-5 w-5 text-accent" /> : <Lock className="h-5 w-5 text-rose-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{it.name}</div>
          <div className="text-xs text-muted-foreground font-mono">{it.start_time}–{it.end_time} · {flex ? "Flexível" : "Indisponível"}{it.note ? ` · ${it.note}` : ""}</div>
        </div>
        <button data-testid={`personal-edit-${it.id}`} onClick={onEdit} className="text-xs text-muted-foreground hover:text-foreground px-2 shrink-0">Editar</button>
      </div>
    );
  }
  const badge = statusBadge(it.my_status);
  const tv = it.travel_from_prev;
  const featured = it.my_status === "confirmado";
  if (it.type === "created_event") {
    const t = it.team || {}; const incomplete = (t.open || 0) > 0;
    return (
      <div data-testid={`agenda-created-${it.id}`} onClick={() => navigate(`/eventos/${it.id}`)}
        className={`flex items-center gap-3 rounded-2xl border p-3.5 cursor-pointer transition-all hover:border-accent/40 ${incomplete ? "border-amber-500/30 bg-amber-500/[0.04]" : "border-[#FF4D00]/30 bg-[#FF4D00]/[0.04]"}`}>
        <div className="h-11 w-11 rounded-xl bg-[#FF4D00]/15 flex items-center justify-center shrink-0"><CalendarClock className="h-5 w-5 text-[#FF4D00]" /></div>
        <div className="font-mono text-center shrink-0 w-14"><div className="text-base font-bold">{it.start_time}</div><div className="text-[10px] text-muted-foreground">{it.end_time}</div></div>
        <div className="flex-1 min-w-0">
          <div className="font-head font-semibold truncate flex items-center gap-1.5">{it.name}{it.origin === "EM_CIMA_DA_HORA" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF4D00]/20 text-[#FF4D00] shrink-0">⚡ EM CIMA</span>}<span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FF4D00]/15 text-[#FF4D00] shrink-0">Criador</span></div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Users className="h-3 w-3 shrink-0" />{t.filled}/{t.slots} na equipe{incomplete ? ` · ${t.open} vaga(s) aberta(s)` : ""}</div>
        </div>
        {incomplete
          ? <span className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-300">Equipe incompleta</span>
          : <span className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-400 inline-flex items-center gap-1"><CheckCheck className="h-3 w-3" />Completa</span>}
      </div>
    );
  }
  return (
    <div>
      {tv && <div className={`flex items-center gap-2 text-xs mb-1.5 ml-2 ${tv.conflict ? "text-rose-400" : "text-muted-foreground"}`}><Car className="h-3.5 w-3.5" />Deslocamento {tv.minutes != null ? `${tv.minutes} min` : "—"} · intervalo {tv.gap_min} min{tv.conflict && <span className="font-semibold"> · insuficiente!</span>}</div>}
      <div data-testid={`agenda-item-${it.id}`} onClick={() => navigate(`/eventos/${it.id}`)}
        className={`flex items-center gap-3 rounded-2xl border p-3.5 cursor-pointer transition-all hover:border-accent/40 ${tv?.conflict ? "border-rose-500/40" : featured ? "border-accent/40 bg-accent/[0.04] shadow-[0_0_18px_rgba(255,170,0,0.1)]" : "border-white/10 bg-card"}`}>
        <div className="h-11 w-11 rounded-xl bg-[#FF4D00]/15 flex items-center justify-center shrink-0"><Music className="h-5 w-5 text-[#FF4D00]" /></div>
        <div className="font-mono text-center shrink-0 w-14"><div className="text-base font-bold">{it.start_time}</div><div className="text-[10px] text-muted-foreground">{it.end_time}</div></div>
        <div className="flex-1 min-w-0">
          <div className="font-head font-semibold truncate">{it.name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{it.address}</div>
          {it.checkin && <div className="text-xs text-emerald-400 flex items-center gap-1 mt-0.5"><CheckCircle2 className="h-3 w-3" />Check-in feito</div>}
        </div>
        {badge && <div className="shrink-0"><StatusBadge {...badge} /></div>}
      </div>
    </div>
  );
};

const ATT_STYLE = {
  alta: { cls: "border-rose-500/30 bg-rose-500/[0.06]", dot: "bg-rose-400", Icon: AlertTriangle, iconCls: "text-rose-400", label: "Prioridade alta" },
  atencao: { cls: "border-amber-500/30 bg-amber-500/[0.06]", dot: "bg-amber-400", Icon: AlertCircle, iconCls: "text-amber-400", label: "Atenção" },
  oportunidade: { cls: "border-sky-500/30 bg-sky-500/[0.06]", dot: "bg-sky-400", Icon: Sparkles, iconCls: "text-sky-400", label: "Oportunidade" },
};

const AttentionItem = ({ it, idx, navigate }) => {
  const s = ATT_STYLE[it.level] || ATT_STYLE.atencao; const Icon = s.Icon;
  return (
    <div data-testid={`attention-item-${idx}`} className={`flex items-center gap-3 rounded-2xl border p-3.5 ${s.cls}`}>
      <div className="h-9 w-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><Icon className={`h-4 w-4 ${s.iconCls}`} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{it.title}</div>
        <p className="text-xs text-muted-foreground truncate">{it.text}</p>
      </div>
      <Button data-testid={`attention-cta-${idx}`} onClick={() => navigate(it.cta_route)} size="sm" variant="outline"
        className="rounded-lg bg-transparent border-primary/30 text-primary gap-1 shrink-0 h-8"><span className="hidden sm:inline">{it.cta_label}</span><ArrowRight className="h-4 w-4" /></Button>
    </div>
  );
};

const SeuDia = ({ insights, utype, onAvailable, onBlock, navigate }) => {
  const d = insights?.your_day;
  const free = insights ? insights.free : null;
  const showOpp = free && (utype === "freelancer" || utype === "both") && (insights?.opportunities || 0) > 0;
  return (
    <section data-testid="seu-dia-card" className="rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.06] to-card p-4">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2"><Sparkles className="h-3.5 w-3.5 text-accent" />Seu dia</div>
      {d ? (
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="font-mono text-center shrink-0 w-14"><div className="text-lg font-bold text-foreground">{d.start_time}</div><div className="text-[10px] text-muted-foreground">{d.end_time}</div></div>
            <div className="min-w-0 flex-1">
              <div className="font-head font-bold truncate flex items-center gap-1.5">{d.name}
                <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-muted-foreground shrink-0">{d.role === "creator" ? "Criador" : d.role === "freelancer" ? "Freelancer" : "Pessoal"}</span>
              </div>
              {d.address && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{d.address}</div>}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                {d.arrival_ref && <span>Chegada {d.arrival_ref}</span>}
                {d.sound_check && <span className="flex items-center gap-1"><Music className="h-3 w-3" />Passagem {d.sound_check}</span>}
                {d.role === "creator" && d.team && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{d.team.filled}/{d.team.slots}{d.team.open > 0 ? ` · ${d.team.open} aberta(s)` : ""}</span>}
                {d.role === "freelancer" && <span>{d.checkin ? "Check-in feito" : d.my_status === "confirmado" ? "Confirmado" : d.my_status === "aprovado" ? "Confirmação pendente" : d.my_status}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="seu-dia-ver-evento" onClick={() => navigate(`/eventos/${d.event_id}`)} size="sm" variant="outline" className="rounded-lg bg-transparent border-white/10 gap-1 h-8 text-xs">Ver evento<ArrowRight className="h-3.5 w-3.5" /></Button>
            {d.more > 0 && <span className="text-xs text-muted-foreground self-center">+{d.more} outro(s) neste dia</span>}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground">Dia livre — nenhum compromisso.</p>
          {showOpp && (
            <button data-testid="seu-dia-oportunidades" onClick={() => navigate("/buscar-eventos")}
              className="w-full text-left rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-3 flex items-center gap-2 hover:border-sky-500/50 transition-colors">
              <Sparkles className="h-4 w-4 text-sky-400 shrink-0" />
              <span className="text-sm text-foreground flex-1 min-w-0">{insights.opportunities} oportunidade(s) combinam com sua disponibilidade.</span>
              <span className="text-xs text-sky-400 font-semibold flex items-center gap-1 shrink-0">Ver <ArrowRight className="h-3.5 w-3.5" /></span>
            </button>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
        <Button data-testid="avail-available-btn" onClick={onAvailable} size="sm" variant="outline" className="flex-1 rounded-xl bg-transparent border-emerald-500/30 text-emerald-400 gap-1.5 h-9"><Unlock className="h-4 w-4" />Estou disponível</Button>
        <Button data-testid="avail-block-btn" onClick={onBlock} size="sm" variant="outline" className="flex-1 rounded-xl bg-transparent border-rose-500/30 text-rose-400 gap-1.5 h-9"><Lock className="h-4 w-4" />Bloquear horário</Button>
      </div>
    </section>
  );
};
